import { PATH_NUMBER } from '@lite-workspace/photobook-model';

export interface BezierAnchor {
  in: [number, number];
  anchor: [number, number];
  out: [number, number];
}

export interface ParsedPath {
  knots: BezierAnchor[];
  closed: boolean;
}

// Numbers read as the arc sampler and `scalePathD` read them (exponent included), so the editor knots sit on the
// curve the sampler measures.
const PATH_TOKEN = new RegExp(`[MLCZ]|${PATH_NUMBER}`, 'g');

function tokenize(d: string): Array<{ cmd: string; args: number[] }> {
  const tokens = d.match(PATH_TOKEN) ?? [];
  const out: Array<{ cmd: string; args: number[] }> = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (/[MLCZ]/.test(t)) {
      const cmd = t;
      i++;
      const argN = cmd === 'M' || cmd === 'L' ? 2 : cmd === 'C' ? 6 : 0;
      const args: number[] = [];
      for (let k = 0; k < argN; k++, i++) args.push(parseFloat(tokens[i]));
      out.push({ cmd, args });
    } else {
      i++;
    }
  }

  return out;
}

export function parsePathD(d: string): ParsedPath {
  const cmds = tokenize(d);
  const knots: BezierAnchor[] = [];
  let closed = false;
  let start: [number, number] = [0, 0];

  const hasZ = cmds[cmds.length - 1]?.cmd === 'Z';

  for (const { cmd, args } of cmds) {
    if (cmd === 'M') {
      const anchor: [number, number] = [args[0], args[1]];
      start = [...anchor] as [number, number];
      knots.push({ in: [...anchor] as [number, number], anchor, out: [...anchor] as [number, number] });
    } else if (cmd === 'C') {
      const cp1: [number, number] = [args[0], args[1]];
      const cp2: [number, number] = [args[2], args[3]];
      const nextAnchor: [number, number] = [args[4], args[5]];
      const last = knots[knots.length - 1];
      if (!last) throw new Error(`parsePathD: '${cmd}' command before 'M'`);
      last.out = cp1;
      const isClosingSegment = hasZ && Math.abs(nextAnchor[0] - start[0]) < 1e-6 && Math.abs(nextAnchor[1] - start[1]) < 1e-6;
      if (isClosingSegment) {
        knots[0].in = cp2;
      } else {
        knots.push({ in: cp2, anchor: nextAnchor, out: [...nextAnchor] as [number, number] });
      }
    } else if (cmd === 'L') {
      const nextAnchor: [number, number] = [args[0], args[1]];
      const last = knots[knots.length - 1];
      if (!last) throw new Error(`parsePathD: '${cmd}' command before 'M'`);
      last.out = [...last.anchor] as [number, number];
      knots.push({ in: [...nextAnchor] as [number, number], anchor: nextAnchor, out: [...nextAnchor] as [number, number] });
    } else if (cmd === 'Z') {
      closed = true;
    }
  }

  return { knots, closed };
}

function fmt(n: number): string {
  const rounded = Math.round(n * 10000) / 10000;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toString();
}

function cubicSegment(a: BezierAnchor, b: BezierAnchor): string {
  return `C ${fmt(a.out[0])} ${fmt(a.out[1])} ${fmt(b.in[0])} ${fmt(b.in[1])} ${fmt(b.anchor[0])} ${fmt(b.anchor[1])}`;
}

export function buildPathD(knots: readonly BezierAnchor[], closed: boolean): string {
  if (knots.length === 0) return '';
  const parts: string[] = [`M ${fmt(knots[0].anchor[0])} ${fmt(knots[0].anchor[1])}`];
  for (let i = 0; i < knots.length - 1; i++) {
    parts.push(cubicSegment(knots[i], knots[i + 1]));
  }

  if (closed && knots.length >= 2) {
    parts.push(cubicSegment(knots[knots.length - 1], knots[0]));
    parts.push('Z');
  }

  return parts.join(' ');
}
